import { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";

import { getPagePath, PAGES } from "../../config/pages";
import type { AppLanguage } from "../../i18n/config";
import {
  detectPreferredLanguage,
  isSupportedLanguage,
} from "../../i18n/routing";
import useSyncLanguage from "../../i18n/useSyncLanguage";
import AppShell from "../AppShell";
import { Cbam, Certif, Demo, Home, NotFound } from "./lazyPages";

function LangRedirect() {
  const language = detectPreferredLanguage();

  return <Navigate to={getPagePath("main", language)} replace />;
}

function LocalizedApp() {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();

  const currentLanguage: AppLanguage | null = isSupportedLanguage(lang)
    ? lang
    : null;

  useSyncLanguage(currentLanguage);

  useEffect(() => {
    if (!currentLanguage) {
      navigate(PAGES.main, { replace: true });
    }
  }, [currentLanguage, navigate]);

  if (!currentLanguage) {
    return null;
  }

  return (
    <Routes>
      {/* The demo is the only landing surface — it renders its own
          institutional chrome (BackStrip / Topbar / Footer), so it lives
          outside AppShell. */}
      {/* Entry hub linking to each demo. */}
      <Route index element={<Home />} />
      {/* Land (foncier) demo — moved here from "/". */}
      <Route path="demos/land" element={<Demo />} />
      {/* Public share URL for a saved demo session. Same Demo component;
          `:workflowId` is read by `useDemoSessionSlug` to hydrate the
          store from the backend timeline. */}
      <Route path="demos/land/:workflowId" element={<Demo />} />
      {/* Certif demo sub-app — renders its own chrome, outside AppShell. */}
      <Route path="demos/certif" element={<Certif />} />
      {/* Cbam demo sub-app — renders its own chrome, outside AppShell. */}
      <Route path="demos/cbam" element={<Cbam />} />
      <Route
        path="*"
        element={
          <AppShell showLangSwitcher>
            <Routes>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppShell>
        }
      />
    </Routes>
  );
}

function I18nRouter() {
  return (
    <Routes>
      <Route path="/:lang/*" element={<LocalizedApp />} />
      <Route path={PAGES.main} element={<LangRedirect />} />
      <Route path="*" element={<LangRedirect />} />
    </Routes>
  );
}

export default I18nRouter;
